<template>
  <div :class="{'auth-google-slide': integrationPlatform === 'GoogleSlide'}">
    <public-announcement />
    <div class="flex items-center justify-center min-h-screen p-4 overflow-hidden">
      <div class="relative z-10 w-100">
        <MultipleLanguage />
        <div class="flex justify-center">
          <span class="pt-10 pb-14" v-if="!$store.state.isPresenterWhiteLabel">
            <a href="https://ahaslides.com">
              <aha-logo
                width="140"
                height="24"
                viewBox="0 0 9600 1602"
              />
            </a>
          </span>
        </div>

        <!-- Form -->
        <div class="shadow-panel rounded-3xl bg-base-0 px-9 pt-10 pb-12">
          <h2 class="mb-4 text-center text-2xl w-64 mx-auto" v-if="isTrialLink">
            {{ $i18nPresenter.t('Sign up for AhaSlides One-month Free trial') }}
          </h2>
          <h2 class="mb-4 text-center text-2xl" v-else>
            {{ $i18nPresenter.t('Sign up') }}
          </h2>

          <h5 class="text-sm text-center mb-7 text-base-90" v-if="!referralUser">
            <p>
              {{ $i18nPresenter.t('Or') }}
              <a @click="goToLogin()" class="font-bold text-link--primary">
                {{ $i18nPresenter.t('Log in to your account') }}
              </a>
            </p>
          </h5>

          <div class="mb-3 text-center" v-if="inviteCodeExpired">
            <h4 class="mb-1 text-lg font-bold">
              {{ $i18nPresenter.t('Your invite link has expired') }}
            </h4>
            <p>{{ $i18nPresenter.t('Please request your inviter for another invite.') }}</p>
          </div>

          <div v-if="!inviteCodeExpired">
            <div v-if="!$store.state.isPresenterWhiteLabel">
              <ThirdPartyButton class="mb-3" type="signup" provider="google">
                {{ $i18nPresenter.t('Sign up with Google') }}
              </ThirdPartyButton>

              <ThirdPartyButton
                type="signup"
                provider="microsoft"
                v-if="!isEnableAddinPowerpoint && integrationPlatform !== 'GoogleSlide'"
              >
                {{ $i18nPresenter.t('Sign up with Microsoft') }}
              </ThirdPartyButton>

              <div class="separator my-6">
                {{ $i18nPresenter.t('Or') }}
              </div>
            </div>

            <form @submit.prevent="onSubmitRegister">
              <aha-input-group class="relative mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="mail" />
                </span>

                <aha-input
                  type="email"
                  class="w-full"
                  :placeholder="$i18nPresenter.t('Your email')"
                  required
                  v-model="email"
                  :disabled="inviteCode"
                />
              </aha-input-group>

              <aha-input-group class="relative mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="lock" />
                </span>

                <aha-input
                  :type="showPassword ? 'text' : 'password'"
                  class="w-full aha-input_limit-char"
                  :placeholder="$i18nPresenter.t('Your password')"
                  size="md"
                  required
                  v-model="password"
                />

                <span
                  class="absolute flex items-center cursor-pointer inset-y-2 right-3"
                  @click="showPassword = !showPassword"
                >
                  <font-awesome-icon
                    class="text-base-80"
                    :icon="!showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                  />
                </span>
              </aha-input-group>

              <aha-input-group class="mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="name" />
                </span>

                <aha-input
                  type="text"
                  class="w-full"
                  :placeholder="$i18nPresenter.t('Your full name')"
                  required
                  v-model="name"
                  size="md"
                />
              </aha-input-group>

              <div class="mb-4 text-sm text-red-100" v-if="textError">
                {{ textError }}
              </div>

              <div class="text-center form-group mb-4">
                <div class="col-xs-12">
                  <vue-recaptcha
                    :sitekey="keyRecaptcha"
                    @verify="onVerify"
                    @error="onRecaptchaError"
                    size="invisible"
                    @expired="onExpired"
                    :load-recaptcha-script="true"
                    recaptcha-host="www.recaptcha.net"
                    ref="recaptcha"
                    class="auth-captcha"
                  />

                  <aha-button
                    type="submit"
                    class="w-full"
                    size="lg"
                    :submitting="submitting"
                    :disabled="isDisabled"
                  >
                    {{ $i18nPresenter.t('Sign Up') }}
                  </aha-button>
                </div>
              </div>

              <p
                class="text-center"
                v-if="
                  !isFromMSTeams
                    &&
                    !$store.state.isPresenterWhiteLabel
                    &&
                    !guestUserId
                    && integrationPlatform !== 'GoogleSlide'"
              >
                <a

                  class="font-bold text-link--primary"
                  @click="handleLoginWithSso()"
                >
                  {{ $i18nPresenter.t('Sign up with SSO') }}
                </a>
              </p>
            </form>
          </div>
        </div>

        <p class="text-sm text-center mt-8 px-8">
          {{ $i18nPresenter.t('By signing up you accept our') }}
          <a href="https://ahaslides.com/terms/" target="_blank" class="primary">
            {{ $i18nPresenter.t('terms of use') }}
          </a> {{ $i18nPresenter.t('and') }}
          <a href="https://ahaslides.com/privacy/" target="_blank" class="primary">
            {{ $i18nPresenter.t('privacy policy') }}
          </a>.
        </p>

        <div class="h-28" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { authentication, app } from '@microsoft/teams-js'
import VueRecaptcha from 'vue-recaptcha'
import * as Sentry from '@sentry/vue'
import { mapActions, mapState } from 'vuex'
import configs from '@/config'
import utils from '@/util'
import ThirdPartyButton from '@/components-v2/common/auth/ThirdPartyButton.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import constants from '@/constant'

// services
import { PRESENTER_REGISTER } from '@/constant/eventCategories'
import cookie from '@/services/cookie'
import { isTrialLink, scheduleShowEnjoyTrialPopup } from '@/utils/trial-plan.utils'
import BulkLicenseMixin from '@/mixins/BulkLicenseMixin.js'
import MultipleLanguage from '@/components-v2/multiple-language/MultipleLanguage.vue'

// api
import UserApi from '@/api/user'

const plans = [
  'essential_annually',
  'pro_annually',
  'essential_monthly',
  'pro_monthly',
  'plus_monthly',
  'plus_annually',
  'edu_small',
  'edu_medium',
  'edu_large',
  'one_time_mini',
  'one_time_small',
  'one_time_medium',
  'one_time_large',
]

export default {
  components: {
    VueRecaptcha,
    ThirdPartyButton,
    PublicAnnouncement,
    MultipleLanguage,
  },
  mixins: [BulkLicenseMixin],
  data() {
    return {
      referralUser: null,
      referralCode: null,
      email: '',
      password: '',
      checkbox_remember_me: true,
      textError: '',
      submitting: false,
      name: '',
      fromPricingPage: false,
      domain: '',
      keyRecaptcha: configs.keyRecapcha,
      tokenRecaptcha: '',
      inviteCode: '',
      inviteCodeExpired: false,
      showPassword: false,
      guestUserId: cookie.getValue('aha-guest-user'),
      signUpLanguage: '',
      isFirstSignUpSession: true,
      isFromMSTeams: false,
      isTrialLink: false,
      isCaptcharLoadError: false,
    }
  },
  computed: {
    ...mapState({
      isPowerPointAddIn: (state) => state.isPowerPointAddIn,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      presenterLanguageSelected: (state) => state.presenterLanguageSelected,
      integrationPlatform: (state) => state.integrationPlatform,
    }),
    saveSpinnerWheel() {
      return utils.getParamValue('spinnerwheel', window.location.href)
    },
    saveWordcloud() {
      return utils.getParamValue('wordcloud', window.location.href)
    },
    isDisabled() {
      return !(this.email || this.password || this.name)
    },
  },
  async created() {
    const pricingSource = window.location.search.indexOf('source=pricing')
    if (pricingSource !== -1) {
      this.fromPricingPage = true
    }

    this.isTrialLink = isTrialLink(window.location.href)

    if (utils.getParamValue('bl_checkout', window.location.href)) {
      const canRedirectToBulkLicenseCheckoutPage = await this.canRedirectToBulkLicenseCheckoutPage()
      if (canRedirectToBulkLicenseCheckoutPage) {
        return
      }
    }
    await this.checkInvitation()
    const token = cookie.getValue('ahaToken')
    if (token && !this.guestUserId) {
      const redirect = utils.getParamValue('redirect', window.location.href)
      if (redirect) {
        window.location.href = redirect

        return
      }
      if (this.integrationPlatform === 'GoogleSlide') {
        return
      }
      this.$router.push('/apps/home')
    }
    this.detectAndSetPageLanguage()
  },
  async mounted() {
    const { 'referral-code': referralCode } = this.$route.query

    this.domain = configs.domain
    this.referralCode = referralCode

    const isFromMSTeams = utils.getParamValue('ms_teams', window.location.href)
    this.isFromMSTeams = !!isFromMSTeams
    if (isFromMSTeams) {
      await app.initialize()
    }

    document.body.classList.remove('no-scroll')
    if (this.$store.state.isPresenterWhiteLabel) {
      this.$router.push({
        path: '/pages/login',
      })
    }

    utils.preloadOnboardingImages()
    // get referral user information
    if (referralCode) {
      try {
        const users = await UserApi.getUsers({ referralCode })
        this.referralUser = users[0] || null
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    utils.mixpanelTracking(
      PRESENTER_REGISTER,
      'Open sign up form',
    )
  },
  methods: {
    ...mapActions([
      'createCheckoutSessionAction',
      'patchCurrentUserAction',
    ]),
    onVerify(response) {
      this.tokenRecaptcha = response
      utils.mixpanelTracking(
        PRESENTER_REGISTER,
        'Recaptcha verified',
      )
      this.register()
    },
    onRecaptchaError() {
      this.isCaptcharLoadError = true
      this.submitting = false
      // this.textError = 'Recaptcha error. If the problem still occurs, please check your internet connection.'
    },
    onSubmitRegister() {
      if (this.submitting) return
      this.submitting = true
      utils.mixpanelTracking(
        PRESENTER_REGISTER,
        'Click Sign up button',
      )
      if (configs.env !== 'production' || this.email.match(/qa\.ahaslides\+(.*)@gmail.com/i) || this.isCaptcharLoadError || this.isPowerPointAddIn) {
        this.register()
      } else {
        // this.register()
        if (!window.grecaptcha) {
          this.isCaptcharLoadError = true
          this.register()
          return
        }
        this.$refs.recaptcha.execute()
        utils.removeRecaptchaCover()
      }
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },

    async checkInvitation() {
      this.inviteCode = utils.getParamValue('invite-code', window.location.href)

      if (this.inviteCode) {
        try {
          const res = await this.$http.get(configs.api.getInvitation + this.inviteCode)
          const { expireAt, invitedEmail, invitedName } = res.body

          if (new Date(expireAt).getTime() < Date.now()) {
            this.inviteCodeExpired = true
          }

          this.email = invitedEmail
          this.name = invitedName
        } catch (err) {
          if (Sentry && err.status) {
            Sentry.captureException(
              `Check invitation code ${this.inviteCode}, error: ${JSON.stringify(err)}`,
            )
          }
        }
      }
    },

    goToLogin() {
      if (this.isEnableAddinPowerpoint) {
        window.location.href = `${configs.presenterDomain}/pages/login`
        return
      }
      const content = utils.getParamValue('content', window.location.href)
      const source = utils.getParamValue('source', window.location.href)
      const promo = utils.getParamValue('promo', window.location.href)
      const code = utils.getParamValue('code', window.location.href)
      const redirect = utils.getParamValue('redirect', window.location.href)
      const isBulkLicenseCheckout = utils.getParamValue('bl_checkout', window.location.href)

      let query = {}

      if (content && source === 'pricing') {
        query = {
          content,
          source,
        }
        if (promo) {
          query.promo = promo
        }
      }
      if (this.isFromMSTeams) {
        query = {
          ms_teams: true,
        }
      }
      if (code) {
        query.code = code
      }
      if (redirect) {
        query.redirect = redirect
      }

      if (isBulkLicenseCheckout) {
        query = {
          bl_checkout: true,
          bl_plan: utils.getParamValue('bl_plan', window.location.href),
          bl_qty: utils.getParamValue('bl_qty', window.location.href),
        }
      }

      this.$router.push({
        path: '/pages/login',
        query,
      })
    },
    isValidSignUpForm() {
      if (this.password.length < 8) {
        this.textError = 'Password needs at least 8 characters.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''

        return false
      }

      if (!utils.validateEmail(this.email)) {
        this.textError = 'Invalid Email.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''

        return false
      }

      if (this.name.length > 40) {
        this.textError = 'Your name must be no longer than 40 characters.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''

        return false
      }

      return true
    },

    async updateLanguage(currentUser) {
      if (currentUser.id) {
        const payload = {
          personalInformation: currentUser.personalInformation || {},
        }
        if (this.presenterLanguageSelected) {
          payload.personalInformation.presenterLanguage = this.presenterLanguageSelected
        } else {
          const index = constants.languageOptions.findIndex((item) => item.country &&
          item.country === currentUser.country)
          if (index !== -1) {
            payload.personalInformation.presenterLanguage = constants.languageOptions[index].language
            this.$store.dispatch('setPresenterLang', constants.languageOptions[index].language)
          }
        }
        if (payload.personalInformation.presenterLanguage) {
          await this.patchCurrentUserAction({
            userId: currentUser.id,
            ...payload,
          })
        }
      }
      this.$store.state.presenterLanguageSelected = null
    },

    async handleRegisterSuccess(res, payload, asGuest = false) {
      cookie.setValue('aha-new-user', true)
      utils.removeFirstPageCookie()
      // user create from signup page and check i'm ready onboarding

      const content = utils.getParamValue('content', window.location.href)
      const promo = utils.getParamValue('promo', window.location.href)
      const redirect = utils.getParamValue('redirect', window.location.href)
      const hasPlan = plans.includes(content)

      if (asGuest) {
        this.$store.dispatch('setCurrentUserAction', res.body)
        utils.mixpanelTracking(
          PRESENTER_REGISTER,
          'Sign up',
          'Demo',
          null,
          {
            signUpLanguage: this.signUpLanguage,
            firstPage: payload.firstPage,
          },
        )
        utils.trackGA4AndMixpanel('sign_up', {
          method: 'Demo',
        })

        cookie.deleteValue('aha-guest-user')
      } else {
        if (!this.saveSpinnerWheel && !this.saveWordcloud) {
          this.$store.state.isComeFromSignupPage = true
        }
        utils.setCookieToken(res.body.token)
        Vue.http.headers.common.Authorization = `Bearer ${res.body.token}`
        this.$store.dispatch('setCurrentUserAction', res.body.user)
        try {
          await this.updateLanguage(res.body.user)
        } catch (error) {
          Sentry.captureException(error)
        }

        if (this.$route.query.from !== 'audience') {
          utils.mixpanelTracking(
            PRESENTER_REGISTER,
            'Sign up',
            `${this.inviteCode ? 'With invite code' : 'Without invite code'}`,
            null,
            {
              signUpLanguage: this.signUpLanguage,
              inviteCode: this.inviteCode,
              firstPage: payload.firstPage,
            },
          )
          utils.trackGA4AndMixpanel('sign_up', {
            method: `${this.inviteCode ? 'With invite code' : 'Without invite code'}`,
          })
        } else {
          utils.handleAudienceTrackingAuthentication(false, true, 'form login', redirect)
        }

        if (this.isFromMSTeams) {
          authentication.notifySuccess(res.body.token)
        }
      }
      window.dataLayer.push({
        event: 'signup',
      })

      if (this.isTrialLink && res.body.user.hasUpgradedToTrial) {
        scheduleShowEnjoyTrialPopup(res.body.user)
      }

      const canRedirectToBulkLicenseCheckoutPage = await this.canRedirectToBulkLicenseCheckoutPage(res.body.user)

      if (canRedirectToBulkLicenseCheckoutPage) {
        return
      }

      if (this.fromPricingPage && hasPlan) {
        const plan = content
          .replace(/_/g, '-')
          .replace(/annually/g, 'yearly')

        let url = `${configs.presenterDomain}/apps/upgrade/${plan}`
        if (promo) {
          url += `?promo=${promo}`
        }

        window.location.href = url

        return
      }
      if (redirect && redirect !== '/apps/presentations') {
        window.location.href = redirect

        return
      }

      if (this.saveSpinnerWheel) {
        const query = {
          spinnerwheel: true,
        }
        this.$router.push({ path: '/save-presentation', query })

        return
      }

      if (this.saveWordcloud) {
        const query = {
          wordcloud: true,
        }
        this.$router.push({ path: '/save-presentation', query })

        return
      }

      if (this.isEnableAddinPowerpoint) {
        window.location.href = `${configs.presenterDomain}/apps/presentations`
      } else if (this.integrationPlatform === 'GoogleSlide') {
        this.$router.push({ name: 'IntegrationGoogleSlide' })
      } else {
        this.$router.push({ name: 'Home' })
      }
      this.submitting = false
      this.tokenRecaptcha = ''
    },

    handleRegisterError(err) {
      this.submitting = false
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''

      if (err.body && err.body.message) {
        this.textError = err.body.message
        this._setTranslateTextError(err.body.message)
        utils.mixpanelTracking(
          PRESENTER_REGISTER,
          'Sign Up Button > Submit > Failed',
          err.body.message,
        )

        return
      }
      this.textError = 'Invalid email or password. Please try again!'
      utils.mixpanelTracking(
        PRESENTER_REGISTER,
        'Sign Up Button > Submit > Invalid',
        JSON.stringify(err),
      )
    },

    _setTranslateTextError(msg) {
      if (msg === 'Email and password are required') {
        this.textError = this.$i18nPresenter.t('Email and password are required')
      }
      if (msg === 'Email already used.') {
        this.textError = this.$i18nPresenter.t('Email already used.')
      }
    },

    async register() {
      this.textError = ''
      const payload = {
        password: this.password,
        email: this.email,
        inviteCode: this.inviteCode,
        referralCode: this.referralCode,
        firstPage: utils.getCookie('ahaFirstPage'),
        isTrial: this.isTrialLink,
      }

      if (this.isCaptcharLoadError || configs.env !== 'production') {
        payload.bypassRecaptcha = true
      }

      const arr = this.name.split(' ')

      if (arr.length === 1) {
        payload.firstName = this.name
      } else {
        [payload.firstName] = arr
        payload.lastName = this.name.replace(`${arr[0]} `, '')
      }

      if (!this.isValidSignUpForm()) {
        this.submitting = false

        return
      }

      const referrer = cookie.getValue('referrer')
      if (referrer) {
        payload.referrer = referrer
      }
      payload.source = window.location.href
      payload.tokenRecaptcha = this.tokenRecaptcha

      if (this.isPowerPointAddIn) {
        payload.bypassRecaptcha = true
      }

      if (this.guestUserId) {
        payload.isGuest = false
        payload.newPassword = payload.password
        try {
          const res = await UserApi.patchUserById(this.guestUserId, payload)
          this.handleRegisterSuccess(res, payload, true)
        } catch (err) {
          Sentry.captureException(err)
          this.handleRegisterError(err)
        }

        return
      }

      // eslint-disable-next-line promise/catch-or-return
      this.$http.post(configs.api.createAccount, payload).then(
        (res) => this.handleRegisterSuccess(res, payload),
        this.handleRegisterError,
      )

      if (this.isCaptcharLoadError) {
        Sentry.captureException(`Presenter register with recaptchar error: ${this.email}`)
      }
    },

    registerUser() {
      if (this.isEnableAddinPowerpoint) {
        window.location.href = `${configs.presenterDomain}/pages/register`
      } else {
        this.$router.push('/pages/register')
      }
    },

    handleLoginWithSso() {
      utils.mixpanelTracking(
        PRESENTER_REGISTER,
        'Click Sign up button',
        'SSO',
      )
      if (this.isEnableAddinPowerpoint) {
        window.location.href = `${configs.presenterDomain}/pages/sso`
      } else {
        this.$router.push({
          path: '/pages/sso',
          query: {
            ...this.$route.query,
            ms_teams: this.isFromMSTeams ? true : undefined,
          },
        })
      }
    },

    detectAndSetPageLanguage() {
      const userLang = navigator.language ? navigator.language : 'en'
      // eslint-disable-next-line prefer-destructuring
      this.signUpLanguage = userLang.split(/-|_/)[0]
      const signUpLang = ['ja', 'es']
      if (!signUpLang.includes(this.signUpLanguage)) return
      this.$store.dispatch('setLang', this.signUpLanguage)
    },
  },
}
</script>

<style lang="scss" scoped>
.logo img {
  max-width: 180px;
  margin-bottom: 25px;
}

.separator {
    display: flex;
    align-items: center;
    text-align: center;
    color: #4A4A4A;
}
.separator::before, .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #EBEBEB;
}
.separator::before {
    margin-right: 1.5em;
}
.separator::after {
    margin-left: 1.5em;
}

.separator {
  @apply text-base-90;
  display: flex;
  align-items: center;
  text-align: center;
}

.separator::before,
.separator::after {
  @apply border-base-30 border-b;
  content: '';
  flex: 1;
}

.separator:not(:empty)::before {
  @apply mr-5;
}

.separator:not(:empty)::after {
  @apply ml-5;
}
.auth-google-slide {
  .shadow-panel {
    padding-left: 12px;
    padding-right: 12px;
    border-radius: 0;
  }
  .min-h-screen.p-4 {
    padding-left: 0;
    padding-right: 0;
  }
  .auth-logo {
    width: 120px;
  }
}
</style>
